// src/dnd/emitter_tool/components/stages/EmitterStage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as PIXI from "pixi.js";
import SpriteManager from "../../engine/SpriteManager.js";
import { createUnifiedEmitterRuntime } from "../../runtime/UnifiedRuntime.js";
import { drawShapeOverlay } from "../../hooks/overlay.js";
import installTargetingManager from "../../engine/TargetingManager.js";
import { buildFogComposite } from "../../hooks/fog.js";
import { cssToStage } from "../../hooks/overlay/shapes/common";

const UI_FIRE_MODE_KEY = "EmitterTool:UI:fireMode";

function pickStructuralEffectSettings(es) {
  const base = es || {};
  return {
    useWalls: base.useWalls !== false,
    wallResponse: base.wallResponse || "stop",
    restitution: Number.isFinite(base.restitution) ? base.restitution : 0,
  };
}

function pickStructuralShapeSettings(s) {
  const x = s || {};
  return {
    radius: x.radius,
    width: x.width,
    height: x.height,
    rotationDeg: x.rotationDeg,
    angleDeg: x.angleDeg,
    spreadDeg: x.spreadDeg,
    minDist: x.minDist,
    maxDist: x.maxDist,
    emissionMode: x.emissionMode,
    edgeBehavior: x.edgeBehavior,
    spawnFromOrigin: x.spawnFromOrigin,
    spawnMode: x.spawnMode,
    feederCount: x.feederCount,
    feederRadiusM: x.feederRadiusM,
    feederJitterPct: x.feederJitterPct,
    startDeg: x.startDeg,
    endDeg: x.endDeg,
    arcSweepDeg: x.arcSweepDeg,
    arcRotationDeg: x.arcRotationDeg,
    rotationAnimEnabled: x.rotationAnimEnabled,
    rotationAnimDir: x.rotationAnimDir,
    rotationAnimSpeedDegS: x.rotationAnimSpeedDegS,
    rotationAffectsParticles: x.rotationAffectsParticles,
    pulseEnabled: x.pulseEnabled,
    pulsePingPong: x.pulsePingPong,
    pulseMinPct: x.pulseMinPct,
    pulseExpandTimeS: x.pulseExpandTimeS,
    pulseHoldTimeS: x.pulseHoldTimeS,
    pulseContractTimeS: x.pulseContractTimeS,
    pulsePhaseOffsetS: x.pulsePhaseOffsetS,
  };
}

function spriteId(sel) {
  if (!sel) return null;
  if (typeof sel === "string") return sel;
  return sel.id || sel.name || null;
}

function layersSignature(layers) {
  if (!Array.isArray(layers)) return "[]";
  const lean = layers.map((L) => ({
    id: L?.id ?? null,
    enabled: L?.enabled !== false,
    aboveParent: !!L?.aboveParent,
    emitterShape: L?.emitterShape ?? null,
  }));
  return JSON.stringify(lean);
}

function subEmittersSignature(effectSettings) {
  const es = effectSettings || {};
  const list = Array.isArray(es.subEmitters) ? es.subEmitters : [];
  const parentIndexRaw = Number.isFinite(es.subEmittersParentIndex) ? Math.floor(es.subEmittersParentIndex) : 0;
  const parentIndex = Math.min(Math.max(0, parentIndexRaw), list.length);
  const lean = list.map((s) => {
    const ov = s?.overrides || {};
    return {
      id: s?.id ?? null,
      enabled: s?.enabled !== false,
      o_shape: ov.hasOwnProperty("emitterShape"),
      o_shapeKeys: ov.shapeSettings ? Object.keys(ov.shapeSettings).sort() : [],
      o_effectKeys: ov.effectSettings ? Object.keys(ov.effectSettings).sort() : [],
      o_sel: ov.hasOwnProperty("selectedSprite"),
      o_multi: Array.isArray(ov.selectedSprites) ? ov.selectedSprites.length : -1,
    };
  });
  return JSON.stringify({
    enabled: es.subEmittersEnabled !== false,
    parentIndex,
    list: lean,
  });
}

function clamp01(v) {
  v = Number(v);
  if (!Number.isFinite(v)) return 0;
  return v < 0 ? 0 : v > 1 ? 1 : 0;
}

function nowSec() {
  if (typeof performance !== "undefined" && performance.now) return performance.now() / 1000;
  return Date.now() / 1000;
}

function pickPulse(src) {
  if (!src) return {};
  const k = [
    "pulseEnabled",
    "pulsePingPong",
    "pulseMinPct",
    "pulseExpandTimeS",
    "pulseHoldTimeS",
    "pulseContractTimeS",
    "pulsePhaseOffsetS",
  ];
  const out = {};
  for (const key of k) if (key in src) out[key] = src[key];
  return out;
}

function numOrNull(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pulseScaleFrom(settings) {
  const s = settings || {};
  if (!s.pulseEnabled) return 1;
  const minPct = Math.max(0, Math.min(0.99, Number(s.pulseMinPct ?? 1)));
  const expandS = Math.max(1e-4, Number(s.pulseExpandTimeS ?? 1.5));
  const holdS = Math.max(0, Number(s.pulseHoldTimeS ?? 0));
  const contractS = Math.max(1e-4, Number(s.pulseContractTimeS ?? 1.0));
  const phase = Number(s.pulsePhaseOffsetS ?? 0);
  const t = nowSec() + phase;
  const cycle = expandS + holdS + contractS;
  if (cycle <= 0) return 1;
  const p = t % cycle;
  if (!s.pulsePingPong) {
    if (p < expandS) {
      const u = p / expandS;
      return minPct + (1 - minPct) * u;
    }
    if (p < expandS + holdS) return 1;
    const u = (p - expandS - holdS) / contractS;
    return 1 - (1 - minPct) * u;
  }
  const u = p / cycle;
  const y = 0.5 - 0.5 * Math.cos(u * Math.PI * 2);
  return minPct + (1 - minPct) * y;
}

// Unified global readers so emitter tool + VTT see the same world
function readGlobalWalls() {
  if (typeof window === "undefined") return { polygons: [], space: "image" };
  try {
    if (window.__PA_WALLS) {
      return { polygons: window.__PA_WALLS, space: "image" };
    }
    if (window.__ET_WALLS) return window.__ET_WALLS;
  } catch {}
  return { polygons: [], space: "image" };
}

function readGlobalToken() {
  if (typeof window === "undefined") return null;
  try {
    const o = window.__PA_SPELL_ORIGIN;
    if (o && Number.isFinite(o.x) && Number.isFinite(o.y)) {
      return {
        id: "spell-origin",
        type: "player",
        x: o.x,
        y: o.y,
        reach: 0,
        reach_units: "metres",
        light_type: "none",
      };
    }
    if (window.__ET_TOKEN) return window.__ET_TOKEN;
  } catch {}
  return null;
}

// Map UI payload (PointToTarget) → PIXI stage coords
function mapUiOriginToStage(app, payload) {
  if (!app || !app.view || !payload) return null;

  let cssX;
  let cssY;

  const viewRect = app.view.getBoundingClientRect();

  if (payload.space === "host" && payload.rect && Number.isFinite(payload.rect.left)) {
    const absX = payload.rect.left + Number(payload.x ?? payload.xCss ?? 0);
    const absY = payload.rect.top + Number(payload.y ?? payload.yCss ?? 0);
    cssX = absX - viewRect.left;
    cssY = absY - viewRect.top;
  } else if (Number.isFinite(payload.clientX) && Number.isFinite(payload.clientY)) {
    cssX = payload.clientX - viewRect.left;
    cssY = payload.clientY - viewRect.top;
  } else if (Number.isFinite(payload.x) && Number.isFinite(payload.y)) {
    cssX = payload.x;
    cssY = payload.y;
  } else {
    return null;
  }

  try {
    const pt = cssToStage(app, cssX, cssY);
    if (pt && Number.isFinite(pt.x) && Number.isFinite(pt.y)) return pt;
  } catch {}
  return null;
}

// Inverse: stage → CSS coords (relative to PIXI view)
function stageToCss(app, x, y) {
  const res = Number(app?.renderer?.resolution) || 1;
  const sx = Number(app?.stage?.scale?.x) || 1;
  const sy = Number(app?.stage?.scale?.y) || 1;
  return { x: x / (res * sx), y: y / (res * sy) };
}

export default function EmitterStage({
  atlas,
  pxPerMeter,
  effectName,
  effectKey,
  selectedSprite,
  effectSettings,
  emitterShape,
  shapeSettings,
  layers,
  originPx,
  originReady,
  showShape,
  mousePx,
  guidePx,
}) {
  const mountRef = useRef(null);
  const appRef = useRef(null);
  const runtimeRef = useRef(null);
  const spriteMgrRef = useRef(null);
  const parentLayerRef = useRef(null);
  const aboveLayerRef = useRef(null);
  const targetingMgrRef = useRef(null);
  const fogRef = useRef(null);
  const installedKeyRef = useRef("");

  const overlayRef = useRef({
    showShape,
    emitterShape,
    shapeSettings,
    pxPerMeter,
    originPx,
    mousePx,
    guidePx,
  });
  useEffect(() => {
    overlayRef.current = { showShape, emitterShape, shapeSettings, pxPerMeter, originPx, mousePx, guidePx };
  }, [showShape, emitterShape, shapeSettings, pxPerMeter, originPx, mousePx, guidePx]);

  const overlayUiCfgRef = useRef({ show: null, settings: {}, originUi: null });
  useEffect(() => {
    const onOverlayCfg = (e) => {
      const d = e?.detail || {};
      const settings = { ...(overlayUiCfgRef.current.settings || {}), ...(d.settings || {}) };
      let originUi = overlayUiCfgRef.current.originUi;
      if (d.origin && (Number.isFinite(d.origin.x) || Number.isFinite(d.origin.xCss))) {
        const o = d.origin;
        originUi = {
          x: Number.isFinite(o.x) ? o.x : o.xCss,
          y: Number.isFinite(o.y) ? o.y : o.yCss,
          space: o.space || null,
          rect: o.rect || null,
          clientX: o.clientX,
          clientY: o.clientY,
        };
      }
      overlayUiCfgRef.current = {
        show: typeof d.show === "boolean" ? d.show : overlayUiCfgRef.current.show,
        settings,
        originUi,
      };
    };
    window.addEventListener("EmitterTool:UI:EmitterOverlayConfig", onOverlayCfg);
    return () => window.removeEventListener("EmitterTool:UI:EmitterOverlayConfig", onOverlayCfg);
  }, []);

  const rulePhaseActiveRef = useRef(false);
  useEffect(() => {
    const onStart = () => {
      rulePhaseActiveRef.current = true;
    };
    const onEnd = () => {
      rulePhaseActiveRef.current = false;
    };
    const onStop = () => {
      rulePhaseActiveRef.current = false;
    };
    window.addEventListener("EmitterTool:RulePhaseStart", onStart);
    window.addEventListener("EmitterTool:RulePhaseEnd", onEnd);
    window.addEventListener("EmitterTool:StopRule", onStop);
    return () => {
      window.removeEventListener("EmitterTool:RulePhaseStart", onStart);
      window.removeEventListener("EmitterTool:RulePhaseEnd", onEnd);
      window.removeEventListener("EmitterTool:StopRule", onStop);
    };
  }, []);

  const [settingsDirtyVersion, setSettingsDirtyVersion] = useState(0);
  useEffect(() => {
    const onDirty = () => setSettingsDirtyVersion((v) => v + 1);
    window.addEventListener("EmitterTool:SettingsDirty", onDirty);
    return () => window.removeEventListener("EmitterTool:SettingsDirty", onDirty);
  }, []);

  const installPreset = useCallback(() => {
    const host = mountRef.current;
    const app = appRef.current;
    const rt = runtimeRef.current;
    const sm = spriteMgrRef.current;
    const parentLayer = parentLayerRef.current;
    const aboveLayer = aboveLayerRef.current;
    if (!host || !app || !rt || !parentLayer || !aboveLayer) return;

    const hostW = Math.max(0, host.clientWidth || 0);
    const hostH = Math.max(0, host.clientHeight || 0);
    if (hostW < 4 || hostH < 4) {
      requestAnimationFrame(installPreset);
      return;
    }

    const texObj = atlas?.textures || {};
    const haveTextures = !!texObj && Object.keys(texObj).length > 0;
    if (selectedSprite && !haveTextures) {
      requestAnimationFrame(installPreset);
      return;
    }

    try {
      if (haveTextures) rt.setTextures(texObj);
    } catch {}
    try {
      if (haveTextures) sm?.setTextures?.(texObj);
    } catch {}

    try {
      parentLayer.removeChildren();
    } catch {}
    try {
      aboveLayer.removeChildren();
    } catch {}

    const structuralShape = pickStructuralShapeSettings(shapeSettings);
    const parent = {
      name: effectName || "Emitter Tool",
      selectedSprite: selectedSprite || undefined,
      effectSettings: effectSettings || {},
      emitterShape: emitterShape || "dot",
      shapeSettings: { ...structuralShape },
      infinite: false,
      infiniteRate: 30,
    };

    const childLayers =
      effectSettings?.subEmittersEnabled !== false && Array.isArray(effectSettings?.subEmitters)
        ? effectSettings.subEmitters
            .filter((s) => s && s.enabled !== false)
            .map((s, idx) => {
              const ov = s.overrides || {};
              const parentIndexRaw = Number.isFinite(effectSettings.subEmittersParentIndex)
                ? Math.floor(effectSettings.subEmittersParentIndex)
                : 0;
              const parentIndex = Math.min(Math.max(0, parentIndexRaw), effectSettings.subEmitters.length);
              const above = idx >= parentIndex;

              // carry rule fields so SubEmitterManager can arm correctly
              const startMode = s.startMode || "immediate";
              const delayMs = Number.isFinite(+s.delayMs) ? +s.delayMs : 0;
              const runForMode = s.runForMode || "inherit";
              const runDurationS = Number.isFinite(+s.runDurationS) ? +s.runDurationS : 0.5;

              const out = {
                id: s.id || s.name || Math.random().toString(36).slice(2),
                enabled: true,
                emitterShape: ov.emitterShape,
                shapeSettings: ov.shapeSettings,
                effectSettings: ov.effectSettings,
                __selectionOverrides: {
                  selectedSprite: ov.selectedSprite,
                  selectedSprites: ov.selectedSprites,
                  selectedAnimFrames: ov.selectedAnimFrames,
                  animFps: ov.animFps,
                },
                startMode,
                delayMs,
                runForMode,
                runDurationS,
                aboveParent: above,
                _source: "subEmitters",
              };

              try {
                console.log("[EmitterStage] baked child", {
                  id: out.id,
                  startMode: out.startMode,
                  delayMs: out.delayMs,
                  runForMode: out.runForMode,
                  runDurationS: out.runDurationS,
                  aboveParent: out.aboveParent,
                });
              } catch {}

              return out;
            })
        : [];

    const preset = {
      name: parent.name,
      pxPerMeter: pxPerMeter || 80,
      parent,
      layers: [...(Array.isArray(layers) ? layers : []), ...childLayers],
    };

    const helpers = {
      imageSize: [hostW, hostH],
      imgToLocal: (x, y) => ({ x, y }),
      pxPerMeter: pxPerMeter || 80,
      getOrigin: () => {
        if (originReady && originPx && Number.isFinite(originPx.x) && Number.isFinite(originPx.y)) {
          return { x: originPx.x, y: originPx.y };
        }
        const tok = readGlobalToken();
        if (tok && Number.isFinite(tok.x) && Number.isFinite(tok.y)) {
          return { x: tok.x, y: tok.y };
        }
        return { x: hostW / 2, y: hostH / 2 };
      },
      getWalls: () => readGlobalWalls(),
      getToken: () => readGlobalToken(),
    };

    installedKeyRef.current = structuralKeyRef.current;

    requestAnimationFrame(() => {
      try {
        rt.runPreset(preset, helpers.getOrigin(), parentLayer, aboveLayer, helpers);
      } catch {}
      try {
        const fm = String(localStorage.getItem(UI_FIRE_MODE_KEY) || "infinite").toLowerCase();
        if (fm === "infinite") {
          try {
            window.dispatchEvent(new CustomEvent("EmitterTool:PowerOn"));
          } catch {}
          try {
            window.dispatchEvent(new CustomEvent("EmitterTool:InfiniteStart"));
          } catch {}
        }
      } catch {}
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:Sequencer:Hydrate", {
            detail: { effectSettings },
          })
        );
      } catch {}
    });
  }, [atlas, effectName, effectSettings, emitterShape, layers, originPx, originReady, pxPerMeter, selectedSprite]);

  const structuralKeyRef = useRef("");
  const structuralKey = useMemo(() => {
    const key = JSON.stringify({
      pxPerMeter,
      effectName,
      effectKey,
      sprite: spriteId(selectedSprite),
      emitterShape,
      shape: pickStructuralShapeSettings(shapeSettings),
      eff: pickStructuralEffectSettings(effectSettings),
      layers: layersSignature(layers),
      subEmitters: subEmittersSignature(effectSettings),
      esDirtyV: settingsDirtyVersion,
    });
    structuralKeyRef.current = key;
    return key;
  }, [pxPerMeter, effectName, effectKey, selectedSprite, emitterShape, shapeSettings, effectSettings, layers, settingsDirtyVersion]);

  useEffect(() => {
    const host = mountRef.current;
    if (!host || appRef.current) return;

    const app = new PIXI.Application({
      backgroundAlpha: 0,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
      resizeTo: host,
      powerPreference: "high-performance",
    });
    host.innerHTML = "";
    host.appendChild(app.view);
    appRef.current = app;
    try {
      app.stage.sortableChildren = true;
    } catch {}
    try {
      app.view.style.pointerEvents = "none";
    } catch {}

    const parentLayer = new PIXI.Container();
    const aboveLayer = new PIXI.Container();
    try {
      parentLayer.sortableChildren = true;
    } catch {}
    try {
      aboveLayer.sortableChildren = true;
    } catch {}
    try {
      parentLayer.zIndex = 1000;
    } catch {}
    try {
      aboveLayer.zIndex = 1500;
    } catch {}
    parentLayerRef.current = parentLayer;
    aboveLayerRef.current = aboveLayer;
    app.stage.addChild(parentLayer);
    app.stage.addChild(aboveLayer);

    const startAlpha = clamp01(effectSettings?.fogAlpha ?? 1);
    const fog = buildFogComposite(app, startAlpha, { initialFeatherBlur: 8 });
    fogRef.current = fog;
    try {
      fog.setGridScale({ pxPerMeter });
    } catch {}
    try {
      fog.setZIndex(2500);
    } catch {}
    app.stage.addChild(fog.container);
    try {
      fog.container.visible = !Boolean(effectSettings?.fogHidden);
    } catch {}
    try {
      fog.redraw();
    } catch {}

    const overlayG = new PIXI.Graphics();
    try {
      overlayG.zIndex = 3000;
    } catch {}
    try {
      overlayG.eventMode = "none";
    } catch {}
    app.stage.addChild(overlayG);
    app.stage.sortChildren?.();

    const texObj = atlas?.textures || {};
    const rt = createUnifiedEmitterRuntime({
      app,
      textures: texObj,
      parentLayer,
      aboveLayer,
      autorun: false,
    });
    runtimeRef.current = rt;
    try {
      rt.setEnvironment?.({ pxPerMeter });
    } catch {}

    const sm = new SpriteManager({
      textures: texObj,
      readFromStorage: true,
      writeToStorage: true,
      listenUIEvents: true,
    });
    spriteMgrRef.current = sm;
    try {
      rt.setSpriteManager(sm);
    } catch {}
    if (texObj && Object.keys(texObj).length > 0) {
      try {
        rt.setTextures(texObj);
      } catch {}
        try {
        sm.setTextures?.(texObj);
      } catch {}
    }

    try {
      targetingMgrRef.current = installTargetingManager({
        host,
        getOrigin: () => {
          const o =
            originReady && originPx && Number.isFinite(originPx.x) && Number.isFinite(originPx.y)
              ? { x: originPx.x, y: originPx.y }
              : (() => {
                  const tok = readGlobalToken();
                  if (tok && Number.isFinite(tok.x) && Number.isFinite(tok.y)) {
                    return { x: tok.x, y: tok.y };
                  }
                  return { x: app.renderer.width / 2, y: app.renderer.height / 2 };
                })();
          const css = stageToCss(app, o.x, o.y);
          return { x: css.x, y: css.y };
        },
      });
    } catch {}

    app.ticker.add((delta) => {
      const dt = delta / 60;
      try {
        runtimeRef.current?.update(dt);
      } catch {}
      try {
        fog.update(dt);
      } catch {}
    });

    const drawOverlayTick = () => {
      const latest = overlayRef.current;
      const panelSettings = latest.shapeSettings || {};
      const uiCfg = overlayUiCfgRef.current || {};
      const uiSettings = uiCfg.settings || {};
      const pulseFromShape = pickPulse(panelSettings);
      const pulseFromEffect = pickPulse(effectSettings);
      const mergedBase = { ...panelSettings, ...uiSettings, ...pulseFromEffect, ...pulseFromShape };

      let tokenOrigin =
        latest.originPx && Number.isFinite(latest.originPx.x) && Number.isFinite(latest.originPx.y)
          ? latest.originPx
          : { x: app.renderer.width / 2, y: app.renderer.height / 2 };

      let overlayCenter = null;
      if (uiSettings.lockCenterOn && uiCfg.originUi) {
        overlayCenter = mapUiOriginToStage(app, uiCfg.originUi);
      }
      if (!overlayCenter && latest.guidePx && Number.isFinite(latest.guidePx.x) && Number.isFinite(latest.guidePx.y)) {
        overlayCenter = mapUiOriginToStage(app, { x: latest.guidePx.x, y: latest.guidePx.y });
      }
      if (!overlayCenter && latest.mousePx && Number.isFinite(latest.mousePx.x) && Number.isFinite(latest.mousePx.y)) {
        overlayCenter = mapUiOriginToStage(app, { x: latest.mousePx.x, y: latest.mousePx.y });
      }
      if (!overlayCenter) overlayCenter = tokenOrigin;

      if (!Number.isFinite(mergedBase.angleDeg) && overlayCenter) {
        const dx = overlayCenter.x - tokenOrigin.x;
        const dy = overlayCenter.y - tokenOrigin.y;
        mergedBase.angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
      }

      const scale = pulseScaleFrom(mergedBase);
      const r = numOrNull(mergedBase.radius);
      const w = numOrNull(mergedBase.width);
      const h = numOrNull(mergedBase.height);
      const mind = numOrNull(mergedBase.minDist);
      const maxd = numOrNull(mergedBase.maxDist);
      const fr = numOrNull(mergedBase.feederRadiusM);
      const mergedScaled = {
        ...mergedBase,
        radius: r != null ? r * scale : mergedBase.radius,
        width: w != null ? w * scale : mergedBase.width,
        height: h != null ? h * scale : mergedBase.height,
        minDist: mind != null ? mind * scale : mergedBase.minDist,
        maxDist: maxd != null ? maxd * scale : mergedBase.maxDist,
        feederRadiusM: fr != null ? fr * scale : mergedBase.feederRadiusM,
        pulseScale: scale,
      };

      const ppm = Number.isFinite(latest.pxPerMeter) ? latest.pxPerMeter : pxPerMeter || 80;
      const overlayShouldShow = typeof uiCfg.show === "boolean" ? uiCfg.show : !!latest.showShape;

      try {
        drawShapeOverlay(overlayG, app, {
          show: rulePhaseActiveRef.current ? false : overlayShouldShow,
          shape: latest.emitterShape || "dot",
          settings: mergedScaled,
          origin: overlayCenter || tokenOrigin,
          center: overlayCenter || tokenOrigin,
          pxPerMeter: ppm,
        });
      } catch {}

      if (!rulePhaseActiveRef.current) {
        try {
          runtimeRef.current?.setExternalTransform?.({
            x: tokenOrigin.x,
            y: tokenOrigin.y,
            angleDeg: Number.isFinite(mergedScaled.angleDeg) ? mergedScaled.angleDeg : mergedScaled.rotationDeg,
          });
        } catch {}
        try {
          const live = {
            radius: mergedScaled.radius,
            width: mergedScaled.width,
            height: mergedScaled.height,
            minDist: mergedScaled.minDist,
            maxDist: mergedScaled.maxDist,
            feederRadiusM: mergedScaled.feederRadiusM,
            spreadDeg: mergedScaled.spreadDeg,
            emissionMode: mergedScaled.emissionMode,
            edgeBehavior: mergedScaled.edgeBehavior,
          };
          runtimeRef.current?.setParentShapeSettings?.(live);
        } catch {}
      }
    };

    app.ticker.add(drawOverlayTick);

    const hostEl = host;
    const ro = new ResizeObserver(() => {
      try {
        const w = Math.max(10, hostEl.clientWidth || 320);
        const h = Math.max(10, hostEl.clientHeight || 180);
        app.renderer.resize(w, h);
      } catch {}
      requestAnimationFrame(installPreset);
      try {
        fog.redraw();
      } catch {}
      app.stage.sortChildren?.();
    });
    ro.observe(hostEl);

    installPreset();

    return () => {
      try {
        ro.disconnect();
      } catch {}
      try {
        app.ticker.remove(drawOverlayTick);
      } catch {}
      try {
        spriteMgrRef.current?.destroy?.();
      } catch {}
      try {
        targetingMgrRef.current?.destroy?.();
      } catch {}
      try {
        runtimeRef.current?.destroy?.();
      } catch {}
      try {
        parentLayerRef.current?.destroy?.({ children: true });
      } catch {}
      try {
        aboveLayerRef.current?.destroy?.({ children: true });
      } catch {}
      try {
        fogRef.current?.destroy?.();
      } catch {}
      try {
        app.destroy(true, { children: true });
      } catch {}
      runtimeRef.current = null;
      spriteMgrRef.current = null;
      targetingMgrRef.current = null;
      appRef.current = null;
      parentLayerRef.current = null;
      aboveLayerRef.current = null;
      fogRef.current = null;
    };
  }, [atlas, effectName, effectSettings, emitterShape, installPreset, originPx, originReady, pxPerMeter, selectedSprite]);

  useEffect(() => {
    try {
      runtimeRef.current?.setEnvironment?.({ pxPerMeter });
    } catch {}
  }, [pxPerMeter]);

  useEffect(() => {
    const rt = runtimeRef.current;
    const sm = spriteMgrRef.current;
    if (!rt) return;
    const texObj = atlas?.textures || {};
    if (Object.keys(texObj).length > 0) {
      try {
        rt.setTextures(texObj);
      } catch {}
      try {
        sm?.setTextures?.(texObj);
      } catch {}
    }
  }, [atlas?.textures]);

  useEffect(() => {
    const fog = fogRef.current;
    if (!fog) return;
    try {
      fog.container.visible = !Boolean(effectSettings?.fogHidden);
    } catch {}
    const a = clamp01(effectSettings?.fogAlpha ?? 1);
    try {
      fog.setAlpha ? fog.setAlpha(a) : (fog.container.alpha = a);
    } catch {
      try {
        fog.container.alpha = a;
      } catch {}
    }
    try {
      fog.setGridScale?.({ pxPerMeter });
    } catch {}
    try {
      fog.setZIndex?.(2500);
    } catch {}
    try {
      appRef.current?.stage?.sortChildren?.();
    } catch {}
  }, [effectSettings?.fogHidden, effectSettings?.fogAlpha, pxPerMeter]);

  useEffect(() => {
    if (installedKeyRef.current !== structuralKey) {
      installedKeyRef.current = structuralKey;
      requestAnimationFrame(installPreset);
    }
  }, [structuralKey, installPreset]);

  useEffect(() => {
    let cancelled = false;
    const pump = () => {
      if (cancelled) return;
      const host = mountRef.current;
      const ok = originReady && Number.isFinite(originPx?.x) && Number.isFinite(originPx?.y);
      const w = host?.clientWidth || 0;
      const h = host?.clientHeight || 0;
      if (ok && w >= 4 && h >= 4) {
        installPreset();
        return;
      }
      requestAnimationFrame(pump);
    };
    pump();
    return () => {
      cancelled = true;
    };
  }, [originReady, originPx, installPreset]);

  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <div ref={mountRef} style={{ position: "absolute", inset: 0 }} />
    </div>
  );
}
